import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
import CommandPalette from "./components/CommandPalette";
import ErrorBoundary from "./components/ErrorBoundary";
import { ConfirmProvider } from "./components/ui/ConfirmProvider";
import { logout, setCredentials } from "./slices/authSlice";
import { useGetMyPermissionsQuery } from "./slices/rbacApiSlice";
import socket, { connectSocket, disconnectSocket } from "./socket";
import ChatDock from "./components/chat/ChatDock";
import TaskAlert from "./components/tasks/TaskAlert";
import TaskDock from "./components/tasks/TaskDock";
import ProductionDock from "./components/production/ProductionDock";
import { toast } from "react-toastify";
import { resetAllScrollPositions } from "./utils/scrollUtils";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const theme = useSelector((state) => state.theme?.theme || "light");

  // 🆕 Apply Dark Mode globally
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  // ดึงสิทธิ์สดทุกครั้งที่เปิดเว็บ แล้วอัปเดตทับของที่เก็บไว้
  //
  // ข้อมูลผู้ใช้ถูกเก็บใน cookie อายุ 7 วัน ตั้งแต่ตอนล็อกอิน แปลว่าถ้าแอดมิน
  // เปลี่ยนแผนกหรือติ๊กสิทธิ์ให้ใครระหว่างนั้น คนนั้นจะยังเห็นเมนูชุดเดิมไป
  // จนกว่าจะล็อกเอาต์แล้วล็อกอินใหม่ — ซึ่งไม่มีใครรู้ว่าต้องทำ กลายเป็นว่า
  // ตั้งค่าถูกแล้วแต่ดูเหมือนระบบไม่ทำงาน
  //
  // เขียนทับเฉพาะสามช่องที่เกี่ยวกับสิทธิ์ ไม่แตะ token หรือข้อมูลอื่นในนั้น
  // และอัปเดตก็ต่อเมื่อค่าต่างจริง ไม่งั้นจะ dispatch วนทุกครั้งที่ render
  const { data: freshAccess } = useGetMyPermissionsQuery(undefined, {
    skip: !userInfo,
  });

  useEffect(() => {
    if (!userInfo || !freshAccess) return;

    // เทียบ flag ด้วย ไม่ใช่แค่ permissions/roles/แผนก
    //
    // เดิมรีเฟรชแค่สามอย่างแรก ทำให้ isStore/isAdmin ที่ได้ตอน login ค้างอยู่
    // ในคุกกี้ 7 วัน ย้ายคนเข้าแผนกแล้วเมนูเปลี่ยนตาม แต่ด่านตรวจที่อ่าน flag
    // (AdminRoute, StoreRoute, ปุ่มอีก 30 กว่าไฟล์) ยังใช้ค่าเก่า ต้องออกแล้ว
    // เข้าใหม่ถึงจะตรงกัน
    const freshFlags = freshAccess.flags || {};
    const flagsSame = Object.keys(freshFlags).every(
      (k) => Boolean(userInfo[k]) === Boolean(freshFlags[k]),
    );

    const same =
      JSON.stringify(userInfo.permissions || []) ===
        JSON.stringify(freshAccess.permissions || []) &&
      JSON.stringify(userInfo.roles || []) ===
        JSON.stringify(freshAccess.roles || []) &&
      (userInfo.primaryDepartment || null) ===
        (freshAccess.primaryDepartment || null) &&
      Boolean(userInfo.rbacEnforce) === Boolean(freshAccess.rbacEnforce) &&
      Boolean(userInfo.mustChangePassword) === Boolean(freshAccess.mustChangePassword) &&
      flagsSame;
    if (same) return;

    dispatch(
      setCredentials({
        ...userInfo,
        ...freshFlags,
        roles: freshAccess.roles || [],
        permissions: freshAccess.permissions || [],
        departments: freshAccess.departments || [],
        primaryDepartment: freshAccess.primaryDepartment || null,
        // ด่านหน้าเว็บบล็อกจริงไหม — แอดมินเปิด/ปิดที่ /admin/org แท็บสิทธิ์ (migration 129)
        rbacEnforce: Boolean(freshAccess.rbacEnforce),
        mustChangePassword: Boolean(freshAccess.mustChangePassword),
      }),
    );
  }, [freshAccess, userInfo, dispatch]);

  // รหัสชั่วคราวจากแอดมิน (migration 130) — ต้องตั้งรหัสใหม่ก่อนใช้หน้าอื่น
  //   หลังบ้านกัน API อื่นไว้แล้ว (authMiddleware) หน้านี้แค่พาไปถูกที่
  const navigate = useNavigate();
  const mustChange = Boolean(userInfo?.mustChangePassword);
  useEffect(() => {
    if (mustChange && location.pathname !== "/change-password") navigate("/change-password", { replace: true });
  }, [mustChange, location.pathname, navigate]);

  // Disable browser's automatic scroll restoration — we manage it ourselves
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // 🆕 Socket.IO lifecycle
  // ล็อกอินแล้ว = ต่อ socket (ใช้คุกกี้ jwt — userInfo ไม่มี token แล้ว ดู socket.js)
  const loggedInId = userInfo?._id ?? userInfo?.id ?? null;
  useEffect(() => {
    if (loggedInId) {
      connectSocket();
    } else {
      disconnectSocket();
    }
  }, [loggedInId]);

  // บอกหลังบ้านว่าอยู่หน้าไหน — หน้า /admin/security แท็บ "ใครออนไลน์" (22 ต.ค. 69 · utils/presence.js)
  //   ส่งตอนเปลี่ยนหน้า และตอน socket ต่อใหม่ (หลังบ้านรีสตาร์ต/เน็ตหลุด)
  useEffect(() => {
    if (!loggedInId) return undefined;
    const send = () => socket.emit("presence:page", location.pathname);
    if (socket.connected) send();
    socket.on("connect", send);
    return () => socket.off("connect", send);
  }, [loggedInId, location.pathname]);

  // Listen for real-time events from socket
  useEffect(() => {
    const onConnect = () => console.log("[socket] connected");
    const onDisconnect = () => console.log("[socket] disconnected");
    const onConnectError = (err) =>
      console.warn("[socket] connect_error:", err?.message || err);

    const onNotification = (notif) => {
      console.log("[socket] notification:", notif);
      // 1) Toast
      if (notif.message) {
        toast.info(notif.message, {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          onClick: () => {
            if (notif.actionUrl) window.location.href = notif.actionUrl;
          },
        });
      }
      // 2) Global event so existing components (e.g. Header bell) can refetch
      window.dispatchEvent(new CustomEvent("workflow_notification", { detail: notif }));
    };

    const onWorkflowUpdate = (data) => {
      console.log("[socket] workflow_update:", data);
      window.dispatchEvent(new CustomEvent("workflow_updated", { detail: data }));
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("notification", onNotification);
    socket.on("workflow_update", onWorkflowUpdate);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("notification", onNotification);
      socket.off("workflow_update", onWorkflowUpdate);
    };
  }, []);

  useEffect(() => {
    const expirationTime = localStorage.getItem("expirationTime");
    if (expirationTime) {
      const currentTime = new Date().getTime();
      if (currentTime > Number(expirationTime)) {
        dispatch(logout());
      }
    }
  }, [dispatch]);

  // หน้า /mini/* = กล่องเล็กสำหรับหน้าต่างลอยบนจอ (เช่น "ปักงานผลิต") — ไม่มีเมนูข้าง หัวเว็บ แชทลอย หรือท้ายเว็บ
  if (location.pathname.startsWith("/mini/")) {
    return (
      <ConfirmProvider>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </ConfirmProvider>
    );
  }

  // ระหว่างถูกบังคับตั้งรหัสใหม่ — ไม่มีเมนู หัวเว็บ แชท ปุ่มลอย (พวกนั้นเรียก API ที่หลังบ้านกันไว้ ได้แต่ข้อผิดพลาด)
  if (mustChange) {
    return (
      <>
        <ToastContainer position="top-center" autoClose={2500} hideProgressBar newestOnTop closeOnClick theme="colored" />
        <ConfirmProvider>
          <ErrorBoundary>
            <div className="min-h-[100dvh] bg-[#f5f5f7]">
              <Outlet />
            </div>
          </ErrorBoundary>
        </ConfirmProvider>
      </>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={2500}
        hideProgressBar={true}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />

      <ConfirmProvider>
        <ErrorBoundary>
          {/* Ctrl+K jump-to-any-screen */}
          <CommandPalette />

          {/* แชทลอย — อยู่ทุกหน้า ซ่อนเองเมื่ออยู่หน้าแชทเต็มจอหรือยังไม่ล็อกอิน */}
          <ChatDock />
          {/* งานถึงตัว — การ์ดเด้งค้างจนกว่าจะกด */}
          <TaskAlert />
          {/* ปุ่มลอย "งานของฉัน" ข้างปุ่มแชท — เปิดเองวันละครั้งถ้ามีงานค้าง */}
          <TaskDock />
          {/* ปุ่มลอย "ติดตามการตรวจ" รอบผลิต — หัวหน้าดูว่าตรวจถึงไหนแล้ว ไม่ต้องเข้าหลายชั้น (16 ต.ค. 69) */}
          <ProductionDock />

          <div className="flex bg-[#f5f5f7] dark:bg-black min-h-[100dvh]">
            {/* Global Sidebar */}
            <Sidebar />

            {/* Main Content Area */}
            <div id="main-scroll-container" className="flex-1 flex flex-col min-w-0 h-[100dvh] overflow-y-auto">
              {/* Global Header */}
              <Header />

              {/* Main Layout */}
              <main className="flex-1 flex flex-col relative w-full overflow-x-hidden">
                <Outlet />
              </main>

              {/* Global Footer */}
              <Footer />
            </div>
          </div>
        </ErrorBoundary>
      </ConfirmProvider>
    </>
  );
};

export default App;
